import React, { useState } from "react";
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, TablePagination,
    useMediaQuery
} from "@mui/material";

// Custom COLORS for a Microsoft-like aesthetic (re-using the consistent palette)
const MS_COLORS = {
    primary: '#2D4A3D',
    primaryLight: '#EBF5EE',
    text: '#323130',
    textSecondary: '#605D5C',
    background: '#FFFFFF',
    backgroundLight: '#F3F2F1',
    border: '#E1E1E1',
    hoverBackground: '#F0F0F0',
    white: '#FFFFFF',
    accent: '#107C10',
    red: '#D83B01',
};

// Component now receives logData as a prop
const AdminActivityLog = ({ logData }) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    const isSmallScreen = useMediaQuery("(max-width:600px)");

    const formatTimestamp = (isoString) => {
        if (!isoString) return "N/A";
        const date = new Date(isoString);
        if (isNaN(date.getTime())) {
            return "Invalid Date";
        }
        const optionsDate = { year: "numeric", month: "short", day: "numeric" };
        const datePart = date.toLocaleDateString("en-US", optionsDate);
        const hours = date.getHours() % 12 || 12;
        const minutes = String(date.getMinutes()).padStart(2, "0");
        const seconds = String(date.getSeconds()).padStart(2, "0");
        const ampm = date.getHours() >= 12 ? "PM" : "AM";
        const timePart = `${hours}:${minutes}:${seconds} ${ampm}`;
        return `${datePart} - ${timePart}`;
    };

    // Handle page change
    const handleChangePage = (event, newPage) => setPage(newPage);

    // Handle rows per page change
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <Box
            sx={{
                backgroundColor: "#fff",
                borderRadius: "16px",
                boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
                border: "1px solid rgba(0,0,0,0.05)",
                p: 3,
                mt: 3,
            }}
        >
            <Typography
                variant="h6"
                sx={{
                    color: "#1a1a2e",
                    mb: 3,
                    fontWeight: 800,
                    fontSize: isSmallScreen ? "1.05rem" : "1.2rem",
                    letterSpacing: "-0.3px",
                }}
            >
                Admin Activity Log
            </Typography>

            {logData.length === 0 ? (
                <Box
                    sx={{
                        textAlign: "center",
                        mt: 3,
                        p: 3,
                        border: `1px dashed ${MS_COLORS.border}`,
                        borderRadius: 1,
                        bgcolor: MS_COLORS.backgroundLight,
                    }}
                >
                    <Typography sx={{ color: MS_COLORS.textSecondary, fontSize: "0.9rem", fontStyle: "italic" }}>
                        No admin activity recorded yet.
                    </Typography>
                </Box>
            ) : (
                <TableContainer
                    component={Paper}
                    sx={{
                        borderRadius: 1,
                        boxShadow: 0,
                        overflowX: "auto",
                        border: `1px solid ${MS_COLORS.border}`,
                    }}
                >
                    <Table sx={{ minWidth: 650 }}>
                        <TableHead sx={{ bgcolor: MS_COLORS.backgroundLight }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem", whiteSpace: "nowrap", color: MS_COLORS.text }}>Admin Name</TableCell>
                                <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem", whiteSpace: "nowrap", color: MS_COLORS.text }}>Activity</TableCell>
                                <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem", whiteSpace: "nowrap", color: MS_COLORS.text }}>Timestamp</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {logData
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((log) => (
                                    <TableRow
                                        key={log.id}
                                        sx={{
                                            "&:last-child td, &:last-child th": { border: 0 },
                                            "&:hover": {
                                                backgroundColor: MS_COLORS.hoverBackground,
                                            },
                                        }}
                                    >
                                        <TableCell sx={{ fontSize: "0.8rem", color: MS_COLORS.textSecondary }}>{log.admin_name}</TableCell>
                                        <TableCell sx={{ fontSize: "0.8rem", color: MS_COLORS.textSecondary, maxWidth: 350, overflow: 'hidden', textOverflow: 'ellipsis' }}>{log.activity}</TableCell>
                                        <TableCell sx={{ fontSize: "0.8rem", color: MS_COLORS.textSecondary }}>{formatTimestamp(log.timestamp)}</TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {logData.length > 0 && (
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={logData.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{
                        "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select, & .MuiTablePagination-actions":
                        {
                            fontSize: "0.8rem",
                            color: MS_COLORS.textSecondary,
                        },
                    }}
                />
            )}
        </Box>
    );
};

export default AdminActivityLog;